import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Button, Dialog, MetaRow } from '@/ui';
import { useDiscussions } from '@/hooks/useDiscussions';
import { discussionHref, discussionsService } from '@/core/services/discussionsService';
import { DiscussionBell } from './DiscussionBell';
import { Hint } from './styles';

const Card = styled(Panel)`
  padding: 16px 20px;
  h2 {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.lg};
  }
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  & + & {
    border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  }
  a {
    flex: 1;
    min-width: 0;
    color: ${({ theme }) => theme.color.textPrimary};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  p {
    margin: 4px 0;
    overflow-wrap: anywhere;
  }
  small {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;
export function FollowedDiscussions({ manage = false }: { manage?: boolean }) {
  const { owner, prefs, follows, ready, error, refresh } = useDiscussions();
  const [all, setAll] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [removing, setRemoving] = useState<number | null>(null);
  const shown = manage ? follows : follows.filter((f) => !f.unavailable);
  useEffect(() => {
    if (!owner) return;
    const refreshVisible = () => {
      if (!document.hidden) void discussionsService.load(owner, true);
    };
    refreshVisible();
    const timer = window.setInterval(refreshVisible, 30_000);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [owner]);
  if (!owner || (!manage && !prefs.showHome)) return null;
  if (!manage && ready && !error && !shown.length) return null;
  const list = (full: boolean) => (
    <>
      {shown.slice(0, full ? 200 : 3).map((f) =>
        f.unavailable ? (
          <Row key={f.id}>
            <span style={{ flex: 1 }}>
              <strong>Unavailable discussion</strong>
              <p>{f.excerpt}</p>
            </span>
            <Button
              variant="ghost"
              disabled={removing !== null}
              onClick={() => {
                if (!owner) return;
                setRemoving(f.id);
                setRemoveError('');
                void discussionsService
                  .unfollow(owner, f.id)
                  .catch(() => setRemoveError('Could not unfollow. Try again.'))
                  .finally(() => setRemoving(null));
              }}
            >
              Unfollow
            </Button>
          </Row>
        ) : (
          <Row key={f.id}>
            <Link to={discussionHref(f.postId, f.firstNewId || f.commentId)}>
              <strong>
                <MetaRow>
                  <span>{f.author}</span>
                  {f.commentId ? <span>Comment thread</span> : null}
                </MetaRow>
              </strong>
              <p>{f.excerpt || 'Discussion'}</p>
              <small>
                <MetaRow>
                  <span>
                    {f.newCount
                      ? `${f.newCount} new ${f.newCount === 1 ? 'reply' : 'replies'}`
                      : 'Caught up'}
                  </span>
                  <span>
                    {f.mode === 'silent' ? 'Following silently' : 'Notifications selected'}
                  </span>
                </MetaRow>
              </small>
            </Link>
            <DiscussionBell postId={f.postId} commentId={f.commentId} />
          </Row>
        ),
      )}
      {ready && !error && !follows.length && (
        <Hint>No followed discussions yet. Use a post or comment’s bell to keep it here.</Hint>
      )}
    </>
  );
  return (
    <Card>
      <h2>{manage ? 'Manage followed discussions' : 'Discussions you follow'}</h2>
      {!manage && <Link to="/discussions">Labels and invitations</Link>}
      <Hint>Latest reply activity first. Your feed sorting below stays separate.</Hint>
      {!ready && <p role="status">Loading discussions…</p>}
      {error && (
        <>
          <p role="alert">{error}</p>
          <Button variant="ghost" onClick={() => void refresh()}>
            Try again
          </Button>
        </>
      )}
      {removeError && <p role="alert">{removeError}</p>}
      {list(manage)}
      {!manage && shown.length > 3 && (
        <Button variant="ghost" onClick={() => setAll(true)}>
          View all ({shown.length})
        </Button>
      )}
      {all && (
        <Dialog open title="Discussions you follow" onClose={() => setAll(false)}>
          {list(true)}
          <Button variant="ghost" onClick={() => setAll(false)}>
            Close
          </Button>
        </Dialog>
      )}
    </Card>
  );
}
